import type { MonthlyLopItem, MonthlyLopSummary } from '../types/leave';

type ObjectValue = Record<string, unknown>;

function days(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function flag(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function parseItem(value: unknown): MonthlyLopItem | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as ObjectValue;
  const leaveTypeId = Number(row.leaveTypeId);
  return {
    leaveTypeId: Number.isFinite(leaveTypeId) && row.leaveTypeId !== null ? leaveTypeId : null,
    leaveTypeName: text(row.leaveTypeName),
    source: text(row.source) ?? '',
    lopDays: days(row.lopDays),
    countsTowardTotal: flag(row.countsTowardTotal),
    isProvisional: flag(row.isProvisional),
  };
}

/** Maps the backend `lopSummary` payload; returns null when it is absent. */
export function parseMonthlyLopSummary(value: unknown): MonthlyLopSummary | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as ObjectValue;
  return {
    payrollMonth: text(row.payrollMonth),
    isCurrentMonth: flag(row.isCurrentMonth),
    totalLopDays: days(row.totalLopDays),
    appliedLopDays: days(row.appliedLopDays),
    pendingAppliedLopDays: days(row.pendingAppliedLopDays),
    negativeBalanceLopDays: days(row.negativeBalanceLopDays),
    leaveShortfallLopDays: days(row.leaveShortfallLopDays),
    lateArrivalLopDays: days(row.lateArrivalLopDays),
    absenceLopDays: days(row.absenceLopDays),
    isProvisional: flag(row.isProvisional),
    items: Array.isArray(row.items)
      ? row.items.map(parseItem).filter((item): item is MonthlyLopItem => item !== null)
      : [],
  };
}

export type LopBreakdownRow = { key: string; label: string; days: number };

/** Non-zero counted sources in display order. */
export function lopBreakdownRows(summary: MonthlyLopSummary): LopBreakdownRow[] {
  return [
    { key: 'negative', label: 'From negative leave balance', days: summary.negativeBalanceLopDays },
    { key: 'applied', label: 'LOP applied and approved', days: summary.appliedLopDays },
    { key: 'shortfall', label: 'Leave beyond available balance', days: summary.leaveShortfallLopDays },
    { key: 'late', label: 'Late arrival deductions', days: summary.lateArrivalLopDays },
    { key: 'absence', label: 'Absence without approved leave', days: summary.absenceLopDays },
  ].filter(row => row.days > 0);
}

/** "October 2026" from a YYYY-MM-DD payroll month. */
export function formatPayrollMonth(value: string | null): string | null {
  const match = value?.match(/^(\d{4})-(\d{2})/);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}
